import React, { useEffect, useRef, useState } from 'react';
import { XMarkIcon, UserCircleIcon } from '@heroicons/react/24/outline';

/**
 * Modal that prompts an anonymous reviewer (anyone with the share link) to enter
 * their name before their first decision/note action. Modeled after the Google
 * Docs "What is your name?" prompt for unauthenticated commenters.
 *
 * Props:
 *   - isOpen
 *   - initialName: pre-fill (e.g. previously saved name when editing)
 *   - actionLabel: short label describing the queued action ("Shortlist Rahul K.")
 *   - isRequired: disables dismiss affordances when name is mandatory to continue
 *   - title / description: override the review-link wording (e.g. the ARM salary form)
 *   - onSubmit(name)
 *   - onCancel()
 */
const ReviewerNameModal = ({
  isOpen,
  initialName = '',
  actionLabel = '',
  isRequired = false,
  title = '',
  description = 'Your name will be recorded when you open this review link and will appear next to every decision and note you make.',
  onSubmit,
  onCancel,
}) => {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setName(initialName || '');
      setError('');
      // Defer focus so animations don't fight us.
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isOpen, initialName]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    const trimmed = String(name || '').trim();
    if (!trimmed) {
      setError('Please enter your name to continue.');
      return;
    }
    if (trimmed.length > 80) {
      setError('Name must be 80 characters or fewer.');
      return;
    }
    onSubmit(trimmed);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <UserCircleIcon className="h-5 w-5 text-[#007BFF] shrink-0" />
            <h2 className="text-base font-semibold text-gray-900 truncate">
              {initialName ? 'Update your name' : title || 'Who are you reviewing as?'}
            </h2>
          </div>
          {!isRequired && (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
              aria-label="Close"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <div>
            <p className="text-sm text-gray-600">{description}</p>
            {actionLabel && (
              <p className="mt-2 text-xs text-gray-500">
                Continuing will record:{' '}
                <span className="font-medium text-gray-800">{actionLabel}</span>
              </p>
            )}
          </div>

          <div>
            <label htmlFor="reviewer-name-input" className="block text-xs font-medium text-gray-700 mb-1">
              Your name
            </label>
            <input
              id="reviewer-name-input"
              ref={inputRef}
              type="text"
              value={name}
              maxLength={80}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError('');
              }}
              data-testid="public-reviewer-name-input"
              placeholder="e.g. Priya Sharma"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF]"
            />
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            {!isRequired && (
              <button
                type="button"
                onClick={onCancel}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              data-testid="public-review-continue-btn"
              className="px-3 py-1.5 text-sm font-semibold text-white bg-[#007BFF] rounded-lg hover:bg-[#0066cc]"
            >
              {initialName ? 'Save' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewerNameModal;
